import React, { useEffect, useMemo, useState } from "react";
import { Table2, X } from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  etiquetaAnioImprevisto,
  etiquetaPeriodoHorizonte,
  etiquetaTrimestreEje,
  MACRO_PUNTO_INICIO,
  MESES_HORIZONTE,
  proyectarMacroColombia,
  TICKS_TRIMESTRE,
} from "../../../data/macroColombiaProyeccion";
import TablaCalculosEscenarios from "./TablaCalculosEscenarios";
import { useEmpresasLanguage } from "../../../context/EmpresasLanguageContext";

const MESES = MESES_HORIZONTE;
const ITERACIONES_MC = 200;

/** Escala la ventaja de gestión profesional vs. operar sin planeación (~3× brecha en caja). */
const FACTOR_BRECHA_GESTION = 3;

function buildCatalog(sim) {
  return {
    tiposImprevisto: sim.imprevistos.map((item, i) => ({
      ...item,
      label: sim.imprevistoLabels[i],
    })),
    shocksExtremos: sim.shocks.map((item, i) => ({
      ...item,
      label: sim.shockLabels[i],
    })),
    escenariosSensibilidad: sim.sensibilidad,
  };
}

const formatCOPValue = (value, lang) =>
  new Intl.NumberFormat(lang === "en" ? "en-US" : "es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);

/** Etiquetas compactas para ejes de gráficas (evita que todo salga "0M" con cifras bajo 1 millón). */
const formatEjeCOP = (value) => {
  if (!Number.isFinite(value)) return "";
  if (value === 0) return "0";

  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (abs >= 1_000_000_000) {
    const billions = abs / 1_000_000_000;
    return `${sign}${billions >= 10 ? billions.toFixed(0) : billions.toFixed(1).replace(/\.0$/, "")}B`;
  }
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    return `${sign}${millions >= 10 ? millions.toFixed(0) : millions.toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(0)}K`;
  }
  return `${Math.round(value)}`;
};

const parseNum = (value) => {
  const n = Number(String(value).replace(/\D/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const percentil = (valores, p) => {
  if (!valores.length) return 0;
  const sorted = [...valores].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((p / 100) * (sorted.length - 1))));
  return sorted[idx];
};

function getAjusteSensibilidadMes(tipo, mes, meses) {
  const t = meses > 1 ? (mes - 1) / (meses - 1) : 0;

  switch (tipo) {
    case "tasasUp":
      return {
        ingresosMult: 1 - 0.008 * t,
        deudaMult: 1 + 0.04 + 0.12 * t,
        gastosVarMult: 1 + 0.015 * t,
        gastosFijosMult: 1 + 0.005 * t,
      };
    case "tasasDown":
      return {
        ingresosMult: 1 + 0.005 * t,
        deudaMult: 1 - 0.03 - 0.05 * t,
        gastosVarMult: 1 - 0.01 * t,
        gastosFijosMult: 1,
      };
    case "ventasDown":
      return {
        ingresosMult: 0.92 - 0.005 * t,
        deudaMult: 1 + 0.02 * t,
        gastosVarMult: 0.98,
        gastosFijosMult: 1 + 0.004 * t,
      };
    case "ventasUp":
      return {
        ingresosMult: 1.1 + 0.02 * t,
        deudaMult: 1,
        gastosVarMult: 1 + 0.03 * t,
        gastosFijosMult: 1 + 0.01 * t,
      };
    default:
      return {
        ingresosMult: 1,
        deudaMult: 1,
        gastosVarMult: 1,
        gastosFijosMult: 1,
      };
  }
}

function flujoNetoMensual(snapshot) {
  return (
    snapshot.ingresos -
    snapshot.gastosFijos -
    snapshot.gastosVariables -
    snapshot.pagoDeuda
  );
}

/** Costo (o alivio) financiero implícito — solo líneas rotativas pequeñas si no hay deuda declarada. */
function deudaImplicitaMensual(snapshot, adj, tipo) {
  const baseFin = snapshot.gastosVariables * 0.08 + snapshot.ingresos * 0.01;
  if (tipo === "tasasDown") {
    return -Math.round(baseFin * Math.min(0.12, 1 - adj.deudaMult));
  }
  if (tipo === "tasasUp") {
    return Math.round(baseFin * Math.max(0, adj.deudaMult - 1));
  }
  return 0;
}

function montoImprevisto(tipo, snapshot, factor) {
  const flujo = flujoNetoMensual(snapshot);
  const margenRef = Math.max(flujo, snapshot.ingresos * 0.04, 1);

  let base;
  if (tipo.affect === "ingreso") {
    base = Math.min(snapshot.ingresos * 0.25, margenRef * 2.5);
  } else if (tipo.base === "fijos") {
    base = Math.min(snapshot.gastosFijos * 0.08, margenRef * 1.8);
  } else {
    base = Math.min(
      snapshot.gastosVariables + snapshot.gastosFijos * 0.05,
      margenRef * 1.8
    );
  }
  return Math.round(base * factor);
}

function cantidadEventosAleatoria() {
  let total = 0;
  for (let a = 0; a < 3; a += 1) {
    const r = Math.random();
    if (r < 0.35) total += 0;
    else if (r < 0.7) total += 1;
    else if (r < 0.9) total += 2;
    else total += 3;
  }
  return Math.min(total, 6);
}

function generarEventosAleatorios(snapshot, catalog) {
  const TIPOS_IMPREVISTO = catalog.tiposImprevisto;
  const SHOCKS_EXTREMOS = catalog.shocksExtremos;
  const cantidad = cantidadEventosAleatoria();
  const eventos = [];
  const allowStacking = Math.random() < 0.12;
  const mesesUsados = new Map();

  for (let i = 0; i < cantidad; i += 1) {
    let mes = Math.floor(Math.random() * MESES) + 1;
    if (!allowStacking) {
      let intentos = 0;
      while (mesesUsados.has(mes) && intentos < 10) {
        mes = Math.floor(Math.random() * MESES) + 1;
        intentos += 1;
      }
    }
    const enMes = mesesUsados.get(mes) ?? 0;
    if (enMes >= 2) continue;
    mesesUsados.set(mes, enMes + 1);

    const tipo = TIPOS_IMPREVISTO[Math.floor(Math.random() * TIPOS_IMPREVISTO.length)];
    const [minF, maxF] = tipo.factor;
    const factor = minF + Math.random() * (maxF - minF);

    eventos.push({
      mes,
      label: tipo.label,
      affect: tipo.affect,
      monto: montoImprevisto(tipo, snapshot, factor),
    });
  }

  if (Math.random() < 0.1) {
    const shock = SHOCKS_EXTREMOS[Math.floor(Math.random() * SHOCKS_EXTREMOS.length)];
    const [minF, maxF] = shock.factor;
    const factor = minF + Math.random() * (maxF - minF);
    eventos.push({
      mes: Math.floor(Math.random() * MESES) + 1,
      label: shock.label,
      affect: shock.affect,
      monto: montoImprevisto(shock, snapshot, factor),
    });
  }

  return eventos;
}

function proyectarEscenario({
  saldoInicial,
  ingresos,
  gastosFijos,
  gastosVariables,
  pagoDeuda,
  eventos = [],
  conGestion,
  sensibilidad = null,
}) {
  let caja = saldoInicial;
  let reserva = 0;
  let ingresoPerdidoPendiente = 0;
  const puntos = [];

  let facturacion = 0;
  let costesDirectos = 0;
  let gastosGenerales = 0;
  let gastosFinancieros = 0;
  let impactoImprevistos = 0;
  let minSaldo = saldoInicial;

  const snapshot = { ingresos, gastosFijos, gastosVariables, pagoDeuda };

  for (let mes = 1; mes <= MESES; mes += 1) {
    const adj = sensibilidad
      ? getAjusteSensibilidadMes(sensibilidad, mes, MESES)
      : { ingresosMult: 1, deudaMult: 1, gastosVarMult: 1, gastosFijosMult: 1 };

    let ingresoMes = ingresos * adj.ingresosMult;
    let deudaMes =
      pagoDeuda > 0
        ? pagoDeuda * adj.deudaMult
        : sensibilidad === "tasasUp" || sensibilidad === "tasasDown"
          ? deudaImplicitaMensual(snapshot, adj, sensibilidad)
          : 0;
    let gastoVarMes = gastosVariables * adj.gastosVarMult;
    let gastoFijoMes = gastosFijos * adj.gastosFijosMult;
    let gastoMes = gastoFijoMes + gastoVarMes + deudaMes;

    const eventosMes = eventos.filter((e) => e.mes === mes);
    let impactoIngreso = 0;
    let impactoGasto = 0;
    for (const ev of eventosMes) {
      if (ev.affect === "ingreso") {
        impactoIngreso += ev.monto;
      } else {
        impactoGasto += ev.monto;
      }
    }

    let gastoImprevisto = 0;

    const f = FACTOR_BRECHA_GESTION;
    const eficienciaVar = Math.max(0.68, 1 - (1 - 0.84) * f);
    const eficienciaDeuda = Math.max(0.88, 1 - (1 - 0.96) * f);
    const tasaReserva = Math.min(0.45, 0.25 + 0.07 * f);
    const coberturaReserva = Math.min(0.95, 0.75 + 0.07 * f);
    const absorcionIngreso = Math.max(0.08, 0.3 / f);
    const arrastreIngreso = Math.max(0.1, 0.45 / f);
    const recuperacionCobranza = Math.min(0.85, 0.55 + 0.08 * f);
    const recortePreventivo = Math.min(0.38, 0.2 + 0.06 * f);
    const driftGastoSinPlan = 0.0015 * f;
    const arrastrePerdidaSinPlan = Math.min(0.58, 0.35 + 0.08 * f);
    const persistenciaPerdidaSinPlan = Math.min(0.52, 0.3 + 0.07 * f);
    const recargoGastoSinPlan = 1 + 0.08 * f;
    const costoSobregiroSinPlan = 0.012 * f;

    if (conGestion) {
      gastoMes = gastoFijoMes + gastoVarMes * eficienciaVar + deudaMes * eficienciaDeuda;
      ingresoMes += ingresoPerdidoPendiente * recuperacionCobranza;
      ingresoPerdidoPendiente = impactoIngreso * arrastreIngreso;

      const excedente = ingresoMes - gastoMes;
      if (excedente > 0) {
        reserva += excedente * tasaReserva;
      }

      gastoImprevisto = impactoGasto;
      const cubierto = Math.min(reserva, gastoImprevisto * coberturaReserva);
      reserva -= cubierto;
      gastoImprevisto -= cubierto;

      ingresoMes -= impactoIngreso * absorcionIngreso;

      const cajaProyectada = caja + ingresoMes - gastoMes - gastoImprevisto;
      if (cajaProyectada < ingresos * 0.15) {
        gastoMes -= gastoVarMes * recortePreventivo;
        deudaMes *= 0.97;
        gastoMes = gastoFijoMes + gastoVarMes * eficienciaVar + deudaMes;
      }

      caja += ingresoMes - gastoMes - gastoImprevisto;
    } else {
      gastoMes *= 1 + (mes - 1) * driftGastoSinPlan;
      ingresoMes -= ingresoPerdidoPendiente * arrastrePerdidaSinPlan;
      ingresoMes -= impactoIngreso;
      ingresoPerdidoPendiente = impactoIngreso * persistenciaPerdidaSinPlan;

      gastoImprevisto = impactoGasto * recargoGastoSinPlan;
      caja += ingresoMes - gastoMes - gastoImprevisto;

      if (caja < 0) {
        const costoSobregiro = Math.min(Math.abs(caja) * costoSobregiroSinPlan, ingresos * 0.015 * f);
        caja -= costoSobregiro;
      }
    }

    facturacion += ingresoMes;
    costesDirectos +=
      gastoVarMes * (conGestion ? eficienciaVar : 1 + (mes - 1) * driftGastoSinPlan) + gastoImprevisto;
    gastosGenerales += gastoFijoMes;
    gastosFinancieros += deudaMes;
    impactoImprevistos += impactoIngreso + impactoGasto + gastoImprevisto;
    minSaldo = Math.min(minSaldo, caja);

    puntos.push({
      idx: mes - 1,
      mes: etiquetaTrimestreEje(mes - 1),
      saldo: Math.round(caja),
      reserva: Math.round(reserva),
    });
  }

  const costesOperativos = costesDirectos + gastosGenerales;
  const totalGastos = costesOperativos + gastosFinancieros;
  const rentabilidad = Math.round(facturacion - totalGastos);
  const margenOperativo = Math.round(facturacion - costesOperativos);

  return {
    puntos,
    resumen: {
      facturacion: Math.round(facturacion),
      costesDirectos: Math.round(costesDirectos),
      gastosGenerales: Math.round(gastosGenerales),
      costesOperativos: Math.round(costesOperativos),
      margenOperativo,
      margenOperativoPct: facturacion ? (margenOperativo / facturacion) * 100 : 0,
      gastosFinancieros: Math.round(gastosFinancieros),
      totalGastos: Math.round(totalGastos),
      rentabilidad,
      minSaldo: Math.round(minSaldo),
      impactoImprevistos: Math.round(impactoImprevistos),
    },
  };
}

function generarLoteEventos(snapshot, catalog, cantidad = ITERACIONES_MC) {
  return Array.from({ length: cantidad }, () => generarEventosAleatorios(snapshot, catalog));
}

function resumenPercentil(resumenes, campo, p) {
  const vals = resumenes.map((r) => r[campo]);
  return Math.round(percentil(vals, p));
}

function ejecutarMonteCarloParado(snapshot, catalog, sensibilidad = null, eventosLote = null) {
  const pathsSin = [];
  const pathsCon = [];
  const resumenesSin = [];
  const resumenesCon = [];
  const pathsConEventos = [];

  for (let i = 0; i < ITERACIONES_MC; i += 1) {
    const eventos = eventosLote ? eventosLote[i] : generarEventosAleatorios(snapshot, catalog);
    const { puntos: pathSin, resumen: resSin } = proyectarEscenario({
      ...snapshot,
      eventos,
      conGestion: false,
      sensibilidad,
    });
    const { puntos: pathCon, resumen: resCon } = proyectarEscenario({
      ...snapshot,
      eventos,
      conGestion: true,
      sensibilidad,
    });
    pathsSin.push(pathSin);
    pathsCon.push(pathCon);
    resumenesSin.push(resSin);
    resumenesCon.push(resCon);
    if (eventos.length > 0) {
      pathsConEventos.push({
        eventos,
        path: pathCon,
        minSaldo: Math.min(...pathCon.map((p) => p.saldo)),
      });
    }
  }

  const bandasDesde = (paths) =>
    Array.from({ length: MESES }, (_, i) => {
      const valores = paths.map((p) => p[i].saldo);
      return {
        idx: i,
        mes: etiquetaTrimestreEje(i),
        p10: Math.round(percentil(valores, 10)),
        p50: Math.round(percentil(valores, 50)),
        p90: Math.round(percentil(valores, 90)),
      };
    });

  const minSaldos = (paths) => paths.map((p) => Math.min(...p.map((pt) => pt.saldo)));

  const minsSin = minSaldos(pathsSin);
  const minsCon = minSaldos(pathsCon);

  pathsConEventos.sort((a, b) => a.minSaldo - b.minSaldo);
  const ejemplos = [];
  if (pathsConEventos.length > 0) {
    ejemplos.push(pathsConEventos[0]);
    ejemplos.push(pathsConEventos[Math.floor(pathsConEventos.length / 2)]);
    ejemplos.push(pathsConEventos[pathsConEventos.length - 1]);
  }

  const unicos = [];
  for (const ex of ejemplos) {
    const key = ex.eventos.map((e) => `${e.mes}-${e.label}`).join("|");
    if (!unicos.some((u) => u.eventos.map((e) => `${e.mes}-${e.label}`).join("|") === key)) {
      unicos.push(ex);
    }
  }

  const resumenDesde = (resumenes, p) => ({
    facturacion: resumenPercentil(resumenes, "facturacion", p),
    costesDirectos: resumenPercentil(resumenes, "costesDirectos", p),
    gastosGenerales: resumenPercentil(resumenes, "gastosGenerales", p),
    costesOperativos: resumenPercentil(resumenes, "costesOperativos", p),
    margenOperativo: resumenPercentil(resumenes, "margenOperativo", p),
    margenOperativoPct: resumenPercentil(resumenes, "margenOperativoPct", p),
    gastosFinancieros: resumenPercentil(resumenes, "gastosFinancieros", p),
    totalGastos: resumenPercentil(resumenes, "totalGastos", p),
    rentabilidad: resumenPercentil(resumenes, "rentabilidad", p),
    minSaldo: resumenPercentil(resumenes, "minSaldo", p),
    impactoImprevistos: resumenPercentil(resumenes, "impactoImprevistos", p),
  });

  return {
    mcSin: {
      bandas: bandasDesde(pathsSin),
      resumen: resumenDesde(resumenesSin, 10),
      probCajaNegativa: pathsSin.filter((p) => p.some((pt) => pt.saldo < 0)).length / ITERACIONES_MC,
      minSaldoP10: Math.round(percentil(minsSin, 10)),
      minSaldoP50: Math.round(percentil(minsSin, 50)),
    },
    mcCon: {
      bandas: bandasDesde(pathsCon),
      resumen: resumenDesde(resumenesCon, 50),
      probCajaNegativa: pathsCon.filter((p) => p.some((pt) => pt.saldo < 0)).length / ITERACIONES_MC,
      minSaldoP10: Math.round(percentil(minsCon, 10)),
      minSaldoP50: Math.round(percentil(minsCon, 50)),
      ejemplos: unicos.slice(0, 3),
    },
  };
}

function agruparEventosPorAnio(eventos) {
  const grupos = {};
  for (const ev of eventos) {
    const anio = etiquetaAnioImprevisto(ev.mes);
    if (!grupos[anio]) grupos[anio] = [];
    grupos[anio].push(ev);
  }
  return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b, "es"));
}

const emptyForm = {
  saldoInicial: "",
  ingresos: "",
  gastosFijos: "",
  gastosVariables: "",
  pagoDeuda: "",
};

export default function SimuladorCaja() {
  const { t, lang } = useEmpresasLanguage();
  const sim = t.gestionCaja.simulador;
  const catalog = useMemo(() => buildCatalog(sim), [sim]);
  const formatCOP = (value) => formatCOPValue(value, lang);
  const escenariosSensibilidad = catalog.escenariosSensibilidad;

  const [form, setForm] = useState(emptyForm);
  const [simulated, setSimulated] = useState(false);
  const [simulationKey, setSimulationKey] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const [sensibilidadActiva, setSensibilidadActiva] = useState("tasasUp");
  const [showPopup, setShowPopup] = useState(false);
  const [showTablaCalculos, setShowTablaCalculos] = useState(false);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const puedeSimular =
    parseNum(form.saldoInicial) > 0 &&
    parseNum(form.ingresos) > 0 &&
    parseNum(form.gastosFijos) >= 0;

  const handleSimular = () => {
    if (!puedeSimular) return;
    setSnapshot({
      saldoInicial: parseNum(form.saldoInicial),
      ingresos: parseNum(form.ingresos),
      gastosFijos: parseNum(form.gastosFijos),
      gastosVariables: parseNum(form.gastosVariables),
      pagoDeuda: parseNum(form.pagoDeuda),
    });
    setSimulated(true);
    setShowPopup(false);
    setSimulationKey((k) => k + 1);
  };

  const handleEditar = () => {
    setSimulated(false);
    setShowPopup(false);
  };

  useEffect(() => {
    if (!simulated) return undefined;
    const timer = setTimeout(() => setShowPopup(true), 30000);
    return () => clearTimeout(timer);
  }, [simulated, simulationKey]);

  const eventosLote = useMemo(() => {
    if (!snapshot || !simulated) return null;
    return generarLoteEventos(snapshot, catalog);
  }, [snapshot, simulated, catalog]);

  const mcBase = useMemo(() => {
    if (!snapshot || !simulated || !eventosLote) return null;
    return ejecutarMonteCarloParado(snapshot, catalog, null, eventosLote);
  }, [snapshot, simulated, eventosLote, catalog]);

  const mcMacro = useMemo(() => {
    if (!snapshot || !simulated || !eventosLote) return null;
    return ejecutarMonteCarloParado(snapshot, catalog, sensibilidadActiva, eventosLote);
  }, [snapshot, simulated, eventosLote, sensibilidadActiva, catalog]);

  const resultados = useMemo(() => {
    if (!snapshot || !simulated || !mcBase || !mcMacro) return null;

    const { puntos: baseSin } = proyectarEscenario({
      ...snapshot,
      eventos: [],
      conGestion: false,
    });
    const { puntos: baseCon, resumen: resumenBaseCon } = proyectarEscenario({
      ...snapshot,
      eventos: [],
      conGestion: true,
    });

    return {
      baseSin,
      baseCon,
      resumenBase: resumenBaseCon,
      mcBase,
      mcMacro,
    };
  }, [snapshot, simulated, mcBase, mcMacro]);

  const deltaMacro = useMemo(() => {
    if (!resultados) return null;
    const { mcBase, mcMacro } = resultados;
    return {
      sin: mcMacro.mcSin.minSaldoP10 - mcBase.mcSin.minSaldoP10,
      con: mcMacro.mcCon.minSaldoP50 - mcBase.mcCon.minSaldoP50,
    };
  }, [resultados]);

  const macro = useMemo(() => {
    if (!simulated) return null;
    return proyectarMacroColombia(MESES);
  }, [simulated]);

  const formatEjeTrimestre = (idx) => etiquetaTrimestreEje(Number(idx));

  const propsEjeTrimestre = {
    dataKey: "idx",
    type: "number",
    domain: [0, MESES - 1],
    ticks: TICKS_TRIMESTRE,
    tickFormatter: formatEjeTrimestre,
    stroke: "#64748b",
    fontSize: 10,
  };

  const macroChart = macro?.map((d, i) => ({ ...d, idx: d.idx ?? i })) ?? [];

  const chartData =
    resultados?.mcMacro.mcCon.bandas.map((b, i) => ({
      idx: i,
      mes: b.mes,
      baseSin: resultados.baseSin[i]?.saldo ?? 0,
      baseCon: resultados.baseCon[i]?.saldo ?? 0,
      p10: resultados.mcMacro.mcCon.bandas[i].p10,
      p50: resultados.mcMacro.mcCon.bandas[i].p50,
      p90: resultados.mcMacro.mcCon.bandas[i].p90,
      mcSinP50: resultados.mcMacro.mcSin.bandas[i].p50,
      mcSinP10: resultados.mcMacro.mcSin.bandas[i].p10,
      refSinP50: resultados.mcBase.mcSin.bandas[i].p50,
      refConP50: resultados.mcBase.mcCon.bandas[i].p50,
      reserva: resultados.baseCon[i]?.reserva ?? 0,
    })) ?? [];

  const sensibilidadLabel =
    escenariosSensibilidad.find((e) => e.id === sensibilidadActiva)?.label ?? "";

  const camposMoneda = sim.campos;

  return (
    <div className="space-y-8 relative">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
        <h3 className="text-xl font-extrabold text-[#0b1f3f] mb-1">{sim.formTitle}</h3>
        <p className="text-slate-600 text-sm mb-6">{sim.formIntro}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {camposMoneda.map(({ id, label, hint }) => (
            <label key={id} className="block">
              <span className="text-sm font-semibold text-[#123a72]">{label}</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder={sim.placeholder}
                value={form[id] ? formatCOP(parseNum(form[id])) : ""}
                onChange={(e) => updateForm(id, String(parseNum(e.target.value)))}
                disabled={simulated}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-[#0b1f3f] font-medium focus:outline-none focus:ring-2 focus:ring-[#1746a2] disabled:bg-slate-100 disabled:text-slate-500"
              />
              <span className="text-xs text-slate-500 mt-1 block">{hint}</span>
            </label>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          {!simulated ? (
            <button
              type="button"
              onClick={handleSimular}
              disabled={!puedeSimular}
              className="rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sim.simular}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleSimular}
                className="rounded-full bg-[#1746a2] px-8 py-3 text-white font-bold hover:bg-[#123a72] transition"
              >
                {sim.resimular}
              </button>
              <button
                type="button"
                onClick={handleEditar}
                className="rounded-full border-2 border-[#1746a2] px-8 py-3 text-[#1746a2] font-bold hover:bg-[#1746a2]/5 transition"
              >
                {sim.editar}
              </button>
            </>
          )}
        </div>
        {!puedeSimular && !simulated && (
          <p className="text-xs text-amber-700 mt-3">
            {sim.validacion}
          </p>
        )}
      </div>

      {simulated && resultados && macro && (
        <>
          <div className="rounded-2xl bg-[#1746a2]/5 border border-[#1746a2]/20 px-5 py-4 text-sm text-[#123a72]">
            <strong>{ITERACIONES_MC} escenarios simulados a {MESES} meses (3 años).</strong>{" "}
            Flujo neto mensual de partida:{" "}
            <strong>
              {formatCOP(
                snapshot.ingresos -
                  snapshot.gastosFijos -
                  snapshot.gastosVariables -
                  snapshot.pagoDeuda
              )}
            </strong>
            . Los imprevistos se calibran sobre ese margen operativo, no sobre la facturación bruta.
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-red-50 border border-red-100 p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-red-700">
                Sin planeación · {sensibilidadLabel}
              </p>
              <p className="mt-2 text-2xl font-extrabold text-red-800">
                {formatCOP(resultados.mcMacro.mcSin.minSaldoP10)}
              </p>
              <p className="text-sm text-red-700 mt-1">Caja mínima MC p10 bajo choque macro</p>
              {deltaMacro && deltaMacro.sin !== 0 && (
                <p className="text-xs text-red-600 mt-2 font-semibold">
                  {deltaMacro.sin > 0 ? "↑" : "↓"} {formatCOP(Math.abs(deltaMacro.sin))} vs escenario base
                </p>
              )}
              <p className="text-xs text-red-600 mt-1">
                {(resultados.mcMacro.mcSin.probCajaNegativa * 100).toFixed(0)}% simulaciones con caja negativa
              </p>
            </div>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                Con gestión · {sensibilidadLabel}
              </p>
              <p className="mt-2 text-2xl font-extrabold text-emerald-800">
                {formatCOP(resultados.mcMacro.mcCon.minSaldoP50)}
              </p>
              <p className="text-sm text-emerald-700 mt-1">Caja mínima MC p50 con gestión bajo choque macro</p>
              {deltaMacro && deltaMacro.con !== 0 && (
                <p className="text-xs text-emerald-600 mt-2 font-semibold">
                  {deltaMacro.con > 0 ? "↑" : "↓"} {formatCOP(Math.abs(deltaMacro.con))} vs escenario base
                </p>
              )}
              <p className="text-xs text-emerald-600 mt-1">
                {(resultados.mcMacro.mcCon.probCajaNegativa * 100).toFixed(0)}% simulaciones con caja negativa
                {resultados.mcMacro.mcSin.probCajaNegativa > resultados.mcMacro.mcCon.probCajaNegativa && (
                  <span className="block mt-1 font-semibold">
                    ↓ {((resultados.mcMacro.mcSin.probCajaNegativa - resultados.mcMacro.mcCon.probCajaNegativa) * 100).toFixed(0)} pp menos riesgo vs. sin planeación
                  </span>
                )}
              </p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Brecha sin vs con gestión</p>
              <p className="mt-2 text-sm font-extrabold text-amber-900 leading-snug">{sensibilidadLabel}</p>
              <p className="mt-2 text-2xl font-extrabold text-amber-900">
                {formatCOP(
                  resultados.mcMacro.mcCon.minSaldoP50 - resultados.mcMacro.mcSin.minSaldoP10
                )}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Diferencial de caja mínima: gestión profesional vs. operar sin planear bajo este choque macro
              </p>
            </div>
          </div>

          {resultados.mcMacro.mcCon.ejemplos.length > 0 && (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-extrabold text-[#0b1f3f] mb-2">
                Ejemplos de imprevistos simulados al azar
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Muestra de imprevistos generados automáticamente, agrupados por año del horizonte (3 años).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {resultados.mcMacro.mcCon.ejemplos.map((ej, idx) => (
                  <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#1746a2] mb-2">
                      Escenario {idx + 1}
                    </p>
                    <ul className="space-y-3 text-sm text-slate-700">
                      {ej.eventos.length === 0 ? (
                        <li>Sin imprevistos en este tramo</li>
                      ) : (
                        agruparEventosPorAnio(ej.eventos).map(([anio, evs]) => (
                          <li key={anio}>
                            <p className="text-xs font-bold uppercase tracking-wide text-[#1746a2] mb-1">
                              {anio}
                            </p>
                            <ul className="space-y-1.5 pl-1">
                              {evs.map((ev, j) => (
                                <li key={j}>
                                  {ev.label} ({formatCOP(ev.monto)})
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))
                      )}
                    </ul>
                    <p className="text-xs text-slate-500 mt-3">
                      Saldo mínimo: {formatCOP(ej.minSaldo)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-extrabold text-[#0b1f3f] mb-2">
              Sensibilidad macroeconómica
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Seleccione un choque macro. La caja neta con y sin gestión se recalcula al instante con los mismos
              imprevistos Monte Carlo — verá cómo se separan las líneas roja y azul respecto a la referencia gris.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              {escenariosSensibilidad.map((esc) => (
                <button
                  key={esc.id}
                  type="button"
                  onClick={() => setSensibilidadActiva(esc.id)}
                  className={`text-left rounded-2xl border p-4 transition ${
                    sensibilidadActiva === esc.id
                      ? "border-[#1746a2] bg-[#1746a2]/5 ring-2 ring-[#1746a2]/30"
                      : "border-slate-200 bg-slate-50 hover:border-[#1746a2]/40"
                  }`}
                >
                  <p className="text-sm font-bold text-[#0b1f3f]">{esc.label}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{esc.descripcion}</p>
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 px-1">
              <div>
                <h4 className="text-base font-bold text-[#0b1f3f] mb-1">
                  Proyección Monte Carlo — bandas de confianza (3 años)
                </h4>
                <p className="text-xs text-slate-500">
                  Choque macro activo: <strong>{sensibilidadLabel}</strong>. Las líneas rojas y azules incluyen
                  imprevistos Monte Carlo bajo ese escenario; las grises punteadas muestran la referencia sin choque
                  macro para comparar el movimiento de la caja neta.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTablaCalculos(true)}
                className="shrink-0 inline-flex items-center gap-2 rounded-xl border-2 border-[#1746a2] bg-white px-4 py-2.5 text-sm font-bold text-[#1746a2] hover:bg-[#1746a2]/5 transition shadow-sm"
              >
                <Table2 className="w-4 h-4" />
                Ver cálculos de escenarios
              </button>
            </div>
            <div className="h-80 sm:h-[28rem] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis {...propsEjeTrimestre} />
                  <YAxis stroke="#64748b" fontSize={11} width={48} tickFormatter={formatEjeCOP} />
                  <Tooltip
                    labelFormatter={(idx) => etiquetaPeriodoHorizonte(Number(idx))}
                    formatter={(value, name) => {
                      const labels = {
                        baseSin: "Base sin imprevistos",
                        baseCon: "Base con gestión",
                        mcSinP50: "Sin planeación + macro (MC p50)",
                        mcSinP10: "Sin planeación + macro (MC p10)",
                        p50: "Con gestión + macro (MC p50)",
                        p10: "Con gestión + macro (MC p10)",
                        p90: "Con gestión + macro (MC p90)",
                        refSinP50: "Referencia sin macro (MC p50 sin plan.)",
                        refConP50: "Referencia sin macro (MC p50 con gest.)",
                      };
                      return [formatCOP(value), labels[name] ?? name];
                    }}
                  />
                  <Legend
                    formatter={(value) => {
                      const labels = {
                        baseSin: "Base sin imprevistos",
                        mcSinP50: "Sin planeación + macro",
                        mcSinP10: "Sin planeación p10 + macro",
                        p50: "Con gestión + macro",
                        p10: "Banda p10 con gestión",
                        p90: "Banda p90 con gestión",
                        refSinP50: "Ref. sin macro (sin plan.)",
                        refConP50: "Ref. sin macro (con gest.)",
                      };
                      return labels[value] ?? value;
                    }}
                  />
                  <Line type="monotone" dataKey="refSinP50" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="2 6" dot={false} />
                  <Line type="monotone" dataKey="refConP50" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="2 6" dot={false} />
                  <Line type="monotone" dataKey="p10" stroke="#93c5fd" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                  <Line type="monotone" dataKey="p90" stroke="#93c5fd" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
                  <Line type="monotone" dataKey="mcSinP10" stroke="#f87171" strokeWidth={2} strokeDasharray="6 3" dot={false} />
                  <Line type="monotone" dataKey="mcSinP50" stroke="#ef4444" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="p50" stroke="#1746a2" strokeWidth={3} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h5 className="text-sm font-bold text-[#0b1f3f] mb-1">Inflación (IPC anual %) — bandas de confianza</h5>
                <p className="text-xs text-slate-500 mb-3">
                  Distribución posible del IPC a 3 años desde {MACRO_PUNTO_INICIO.inflacionAnual}% (
                  {MACRO_PUNTO_INICIO.etiqueta}). El abanico se ensancha con el horizonte (σ creciente):
                  hoy hay poca incertidumbre; a 3 años la campana p5–p95 es mucho más amplia.
                </p>
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={macroChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis {...propsEjeTrimestre} />
                      <YAxis stroke="#64748b" fontSize={10} domain={["auto", "auto"]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        labelFormatter={(idx) => etiquetaPeriodoHorizonte(Number(idx))}
                        formatter={(v, name) => {
                          const labels = {
                            p50: "Mediana IPC",
                            p5: "Percentil 5",
                            p95: "Percentil 95",
                            bandP5P10: "Banda p5–p10",
                            bandP10P25: "Banda p10–p25",
                            bandP25P50: "Banda p25–p50",
                            bandP50P75: "Banda p50–p75",
                            bandP75P90: "Banda p75–p90",
                            bandP90P95: "Banda p90–p95",
                          };
                          return [`${Number(v).toFixed(2)}%`, labels[name] ?? name];
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          const labels = { p50: "Mediana IPC (escenario tipo)" };
                          return labels[value] ?? null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="stackBase"
                        stackId="ipc"
                        stroke="none"
                        fill="transparent"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP5P10"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(139, 92, 246, 0.35)"
                        name="bandP5P10"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP10P25"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(99, 102, 241, 0.4)"
                        name="bandP10P25"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP25P50"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(34, 197, 94, 0.35)"
                        name="bandP25P50"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP50P75"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(234, 179, 8, 0.4)"
                        name="bandP50P75"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP75P90"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(249, 115, 22, 0.4)"
                        name="bandP75P90"
                        legendType="none"
                      />
                      <Area
                        type="monotone"
                        dataKey="bandP90P95"
                        stackId="ipc"
                        stroke="none"
                        fill="rgba(239, 68, 68, 0.35)"
                        name="bandP90P95"
                        legendType="none"
                      />
                      <Line type="monotone" dataKey="p50" stroke="#f59e0b" strokeWidth={2.5} dot={false} name="p50" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-violet-400/50" /> p5–p10
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-indigo-400/50" /> p10–p25
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-green-400/50" /> p25–p50
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-yellow-400/50" /> p50–p75
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-orange-400/50" /> p75–p90
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-3 h-3 rounded-sm bg-red-400/50" /> p90–p95
                  </span>
                </div>
              </div>
              <div>
                <h5 className="text-sm font-bold text-[#0b1f3f] mb-1">Actividad privada y tasas de interés</h5>
                <p className="text-xs text-slate-500 mb-3">
                  Índice actividad (base 100) y tasa BanRep a 3 años desde {MACRO_PUNTO_INICIO.tasaInteres}%
                </p>
                <div className="h-52 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={macroChart} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis {...propsEjeTrimestre} />
                      <YAxis yAxisId="act" stroke="#64748b" fontSize={10} domain={["auto", "auto"]} />
                      <YAxis
                        yAxisId="tasa"
                        orientation="right"
                        stroke="#64748b"
                        fontSize={10}
                        domain={["auto", "auto"]}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        labelFormatter={(idx) => etiquetaPeriodoHorizonte(Number(idx))}
                        formatter={(value, name) => {
                          if (name === "actividadPrivada") return [value, "Actividad sector privado"];
                          if (name === "tasaInteres") return [`${value}%`, "Tasa de interés"];
                          return [value, name];
                        }}
                      />
                      <Legend
                        formatter={(value) =>
                          value === "actividadPrivada" ? "Actividad privada" : "Tasa de interés"
                        }
                      />
                      <Line
                        yAxisId="act"
                        type="monotone"
                        dataKey="actividadPrivada"
                        stroke="#1746a2"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                      />
                      <Line
                        yAxisId="tasa"
                        type="monotone"
                        dataKey="tasaInteres"
                        stroke="#ef4444"
                        strokeWidth={2}
                        strokeDasharray="5 3"
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
            <h3 className="text-lg font-extrabold text-[#0b1f3f] mb-4">
              Escenario base — composición con gestión (sin imprevistos)
            </h3>
            <div className="h-56 sm:h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData.map((d, i) => ({
                    idx: i,
                    mes: d.mes,
                    caja: resultados.baseCon[i]?.saldo ?? 0,
                    reserva: resultados.baseCon[i]?.reserva ?? 0,
                  }))}
                  margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis {...propsEjeTrimestre} />
                  <YAxis stroke="#64748b" fontSize={11} width={48} tickFormatter={formatEjeCOP} />
                  <Tooltip
                    labelFormatter={(idx) => etiquetaPeriodoHorizonte(Number(idx))} formatter={(value, name) => [formatCOP(value), name === "caja" ? "Caja operativa" : "Reserva"]} />
                  <Legend formatter={(value) => (value === "caja" ? "Caja operativa" : "Reserva acumulada")} />
                  <Line type="monotone" dataKey="caja" stroke="#1746a2" strokeWidth={3} dot={false} name="caja" />
                  <Line type="monotone" dataKey="reserva" stroke="#93c5fd" strokeWidth={2} strokeDasharray="4 4" dot={false} name="reserva" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      <TablaCalculosEscenarios
        open={showTablaCalculos}
        onClose={() => setShowTablaCalculos(false)}
        snapshot={snapshot}
        resultados={
          resultados
            ? {
                resumenBase: resultados.resumenBase,
                mcSin: resultados.mcMacro.mcSin,
                mcCon: resultados.mcMacro.mcCon,
              }
            : null
        }
        meses={MESES}
      />

      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowPopup(false)}
            aria-hidden
          />
          <div role="dialog" aria-labelledby="popup-asesoria-titulo" className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-8">
            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <p className="text-xs font-bold uppercase tracking-widest text-[#1746a2]">Asesoría sin costo</p>
            <h3 id="popup-asesoria-titulo" className="mt-2 text-2xl font-extrabold text-[#0b1f3f] leading-snug">
              ¿Le gustaría revisar estos escenarios con un especialista?
            </h3>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Agende una asesoría gratuita por WhatsApp. Revisamos juntos sus números, los riesgos de caja y
              cómo la dirección de innovación financiera puede apoyar decisiones informadas en su empresa.
            </p>
            <a
              href={t.gestionCaja.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex w-full items-center justify-center rounded-full bg-[#25D366] px-6 py-3.5 text-white font-bold hover:bg-[#1da851] transition"
            >
              Escribir por WhatsApp
            </a>
            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="mt-3 w-full text-sm text-slate-500 hover:text-slate-700 font-medium"
            >
              Ahora no, gracias
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
